import {
  checkEditorCodeKey,
  checkEditorNameKey,
  deriveEditorSubKey,
  openEditEditor,
  openNewMessageEditor,
  openReplyEditor,
  saveEditorDraft,
  saveEditorMessage,
  toggleEditorMoreSettings,
  toggleEditorCodeKey,
  toggleEditorPinProtection,
  toggleRichEditor,
  toggleRichEditorBar,
} from '@/msgdeskRuntime/legacyBridge/legacyActions'
import {
  collectEditorRecipientsFromLegacy,
  checkEditorSmartNameKey,
  commitEditorRecipient,
  flushEditorModelToLegacyRough,
  generateEditorSmartNameKey,
  removeEditorRecipientFromLegacy,
  setEditorBodyHtml,
  setEditorBodyText,
  setEditorCodeKey,
  setEditorExtendedNameKey,
  setEditorMoreSetting,
  setEditorProductCreation,
  setEditorRulingValue,
  setEditorSectionState,
  setEditorSmartNameKey,
  setEditorNameScope,
  setEditorSubject,
  syncEditorInputElement,
  syncEditorRoughModel,
} from './facade/EditorRoughFacade'
import {
  currentAvatarSrc,
  toggleAttachmentActive,
  uploadAttachment,
  uploadAvatar,
} from './facade/EditorAttachmentsFacade'
import type { EditorActionRequest, EditorNameKeyResult, EditorRecipientCommitResult, EditorRecipientSnapshot } from './contracts/editorActions'
import type { EditorFormModel } from './contracts/editorModel'
import type { EditorAttachmentState } from './state'

export * from './contracts/editorActions'

export function dispatchEditorAction(
  request: Extract<EditorActionRequest, { action: 'commit-recipient' }>,
): Promise<EditorRecipientCommitResult>
export function dispatchEditorAction(
  request: Extract<EditorActionRequest, { action: 'collect-recipients' }>,
): EditorRecipientSnapshot[]
export function dispatchEditorAction(
  request: Extract<EditorActionRequest, { action: 'upload-attachment' | 'upload-avatar' }>,
): Promise<void>
export function dispatchEditorAction(
  request: Extract<EditorActionRequest, { action: 'current-avatar-src' }>,
): string
export function dispatchEditorAction(
  request: Extract<EditorActionRequest, { action: 'check-smart-name-key' | 'check-extended-name-key' | 'generate-smart-name-key' }>,
): Promise<EditorNameKeyResult>
export function dispatchEditorAction(
  request: { action: 'sync-model'; reason?: string },
): EditorFormModel | undefined
export function dispatchEditorAction(
  request: { action: 'flush-model'; reason?: string },
): boolean
export function dispatchEditorAction(request: EditorActionRequest): unknown

export function dispatchEditorAction(request: EditorActionRequest): unknown {
  switch (request.action) {
    case 'open-new-message':
      return openNewMessageEditor()
    case 'open-edit-message':
      return openEditEditor()
    case 'open-reply':
      return openReplyEditor()
    case 'save':
      flushEditorModelToLegacyRough(request.reason || 'editor-action:before-save')
      return saveEditorMessage(request.target)
    case 'save-draft': {
      flushEditorModelToLegacyRough(request.reason || 'editor-action:before-save-draft')
      const result = saveEditorDraft(request.target)
      // Legacy m35 _do_save: "save_draft" TOGGLES drafting mode (Save then
      // performs the draft save). Re-project the rough so the UI can render
      // the armed state — it was invisible before (audit 2026-07-19 D3).
      // The command may be QUEUED through the operation gate; sync only after
      // it actually ran, else the projection captures the pre-toggle state.
      void Promise.resolve(result).then(() => syncEditorRoughModel('editor-action:after-draft-toggle'))
      return result
    }
    case 'toggle-settings':
      return toggleEditorMoreSettings(request.target, request.tabId)
    case 'toggle-pin':
      return toggleEditorPinProtection(request.target, request.pin === 'show' ? 0 : 1)
    case 'sync-model':
      return syncEditorRoughModel(request.reason)
    case 'flush-model':
      return flushEditorModelToLegacyRough(request.reason)
    case 'sync-input':
      return syncEditorInputElement(request.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, request.reason)
    case 'set-body-text':
      return setEditorBodyText(request.value)
    case 'set-more-setting':
      return setEditorMoreSetting(request.name, request.value, request.reason)
    case 'set-product-creation':
      return setEditorProductCreation(request.values, request.reason)
    case 'set-ruling':
      return setEditorRulingValue(request.field, request.value, request.reason)
    case 'set-section':
      return setEditorSectionState(
        request.section,
        { open: request.open, activeTab: request.activeTab },
        request.reason,
      )
    case 'set-subject':
      return setEditorSubject(request.value)
    case 'set-code-key':
      return setEditorCodeKey(request.value)
    case 'set-extended-name-key':
      return setEditorExtendedNameKey(request.value)
    case 'set-smart-name-key':
      return setEditorSmartNameKey(request.value)
    case 'set-name-scope':
      return setEditorNameScope(request.enabled)
    case 'commit-recipient':
      return commitEditorRecipient(request.value)
    case 'remove-recipient':
      return removeEditorRecipientFromLegacy(request.id)
    case 'collect-recipients':
      return collectEditorRecipientsFromLegacy()
    case 'toggle-attachment':
      return toggleAttachmentActive(request.attachment as EditorAttachmentState, request.active)
    case 'upload-attachment':
      return uploadAttachment(request.file as File)
    case 'current-avatar-src':
      return currentAvatarSrc()
    case 'upload-avatar':
      return uploadAvatar(request.file as File)
    case 'check-smart-name-key':
      return checkEditorSmartNameKey(request.value)
    case 'check-extended-name-key':
      return checkEditorSmartNameKey(request.value, true)
    case 'generate-smart-name-key':
      return generateEditorSmartNameKey(request.rule)
    case 'derive-sub-key':
      return deriveEditorSubKey(request.target)
    case 'check-name-key':
      return checkEditorNameKey(request.target)
    case 'check-code-key':
      return checkEditorCodeKey(request.target)
    case 'toggle-code-key':
      return toggleEditorCodeKey(request.open)
    case 'toggle-rich-editor':
      return toggleRichEditor(request.target)
    case 'toggle-rich-bar':
      return toggleRichEditorBar(request.target, request.barId)
    case 'set-body-html':
      return setEditorBodyHtml(request.isHtml, request.value, request.reason)
  }
}
